import type { FC } from "react";
import { useRef, useState, useEffect } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { StructureModal, Textfield } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { isValidUrl } from "../../../wizard/wizard-pages/create-project-step/utils";

import type { ISetTargetUrlModalProps } from "./SetTargetUrlModalConsts";
import { targetUrlProModalStyles } from "./SetTargetUrlModalConsts";
import {
    SetTargetUrlBannerWrapper,
    SetTargetUrlTextFieldWrapper,
    BannerTagsIndicationContent,
} from "./SetTargetUrlModalStyled";

export const SetTargetUrlModal: FC<ISetTargetUrlModalProps> = ({
    isOpen,
    onCancel,
    onSave,
    initialUrl = "",
    isInitialUrlChanged = false,
    showBulkEditBanner = false,
}) => {
    const [targetUrl, setTargetUrl] = useState("");
    const wasTargetUrlChanged = useRef(isInitialUrlChanged);

    useEffect(() => {
        if (isOpen) {
            setTargetUrl(initialUrl);
            wasTargetUrlChanged.current = isInitialUrlChanged;
        }
    }, [isOpen, initialUrl, isInitialUrlChanged]);

    const onUrlTextChange = (value: string): void => {
        // Remove slash before query params (?) and at the end of the url
        const cleanedUrl = value.replace(/\/(\?|$)/g, "$1");
        wasTargetUrlChanged.current = true;
        setTargetUrl(cleanedUrl);
    };

    const handleOnSaveClick = () => {
        if (wasTargetUrlChanged.current) {
            allTrackers.dispatchTrackEvent("Pop Up", "submit-ok", `Set Target URL/${targetUrl}`);
            onSave(targetUrl);
            wasTargetUrlChanged.current = false;
        } else {
            onCancel();
        }
    };

    const handleOnCancelClick = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "close", "Target URLs modal closed by cancel");
        onCancel();
        wasTargetUrlChanged.current = false;
    };

    const isButtonDisabled = targetUrl ? !isValidUrl(targetUrl) : false;

    return (
        <StructureModal
            title={translate("rankTracker.wizard.targetUrl.modal.title")}
            subTitle={translate("rankTracker.wizard.targetUrl.modal.subtitle")}
            mainButton={{
                label: translate("rankTracker.wizard.targetUrl.modal.mainButton"),
                onClick: handleOnSaveClick,
                type: "primary",
                tooltipEnabled: false,
                isDisabled: isButtonDisabled,
            }}
            secondaryButton={{
                label: translate("rankTracker.wizard.targetUrl.modal.cancelButton"),
                onClick: handleOnCancelClick,
                type: "flat",
                tooltipEnabled: false,
            }}
            isOpen={isOpen}
            closeIconConfig={{ show: false }}
            showHeaderDivider={false}
            contentPadding="0"
            customStyles={targetUrlProModalStyles}
            titleTooltipContent={translate("rankTracker.wizard.targetUrl.modal.title.tooltip")}
        >
            <SetTargetUrlTextFieldWrapper data-automation={"target-url-input"}>
                <Textfield
                    defaultValue={initialUrl}
                    dataAutomation="set-target-url-modal-input"
                    placeholder={translate("rankTracker.wizard.targetUrl.modal.placeholder")}
                    onChange={onUrlTextChange}
                />
            </SetTargetUrlTextFieldWrapper>
            {showBulkEditBanner ? (
                <SetTargetUrlBannerWrapper>
                    <BannerTagsIndicationContent>
                        <SWReactIcons
                            iconName="alerts"
                            size="xs"
                            iconColor={colorsPalettes.carbon[200]}
                        />
                        {translate("rankTracker.wizard.targetUrl.modal.banner")}
                    </BannerTagsIndicationContent>
                </SetTargetUrlBannerWrapper>
            ) : null}
        </StructureModal>
    );
};
